"use client";

import { BrandLockup } from "@/components/brand/BrandLockup";
import { DemoVideo } from "@/components/landing/DemoVideo";
import { AFFILIATE_PROGRAM } from "@/lib/affiliate-program";
import { trackEventWithAttribution } from "@/lib/analytics/posthog-helpers";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Check,
  FileText,
  Languages,
  Link2,
  Mail,
  MessageCircle,
  Mic2,
  MousePointerClick,
  ShieldCheck,
  Users,
  Video,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";

const AFFILIATE_CTA_HREF = "/affiliateonboarding";

const OFFER_FACTS = [
  {
    value: `${AFFILIATE_PROGRAM.commissionPercent}%`,
    label: "of net subscription revenue",
  },
  {
    value: `${AFFILIATE_PROGRAM.commissionDurationMonths} months`,
    label: "of eligible recurring commission",
  },
  {
    value: `${AFFILIATE_PROGRAM.attributionWindowDays} days`,
    label: "after a valid affiliate click",
  },
  {
    value: `€${AFFILIATE_PROGRAM.payoutThresholdEur}`,
    label: "minimum validated balance",
  },
] as const;

const PRODUCT_POINTS = [
  {
    icon: Mic2,
    title: "Real voice practice",
    description: "Learners speak out loud with an AI conversation partner whenever they have time.",
  },
  {
    icon: MessageCircle,
    title: "Help during the conversation",
    description:
      "Prompts, hints, and corrections help learners keep going and try a clearer version.",
  },
  {
    icon: Languages,
    title: "70+ languages",
    description:
      "Your audience can practise the language that matters to work, travel, study, or home.",
  },
] as const;

const PROMOTION_ANGLES = [
  {
    audience: "For language creators",
    copy: "Show a real speaking prompt, share your reaction, and give viewers your tracked link.",
  },
  {
    audience: "For teachers and coaches",
    copy: "Recommend low-pressure speaking practice that learners can use between lessons.",
  },
  {
    audience: "For travel and global communities",
    copy: "Connect speaking confidence to the trip, move, course, or conversation ahead.",
  },
] as const;

const HOW_IT_WORKS = [
  {
    icon: Users,
    title: "Join",
    description: "Sign in, accept the current program terms, and create your affiliate profile.",
  },
  {
    icon: Link2,
    title: "Share",
    description:
      "Your personal short link is ready for videos, articles, newsletters, and communities.",
  },
  {
    icon: MousePointerClick,
    title: "Track clicks",
    description:
      "See first-party link activity in your dashboard while you test different campaigns.",
  },
  {
    icon: ShieldCheck,
    title: "Prepare payout",
    description:
      "Secure commission, tax, and payout details will appear when the provider is connected.",
  },
] as const;

const TOOLKIT_ITEMS = [
  { icon: Video, label: "Product demo and short-form speaking clip" },
  { icon: FileText, label: "Approved claims, talking points, and disclosure examples" },
  { icon: Mail, label: "Starter copy for social posts and email" },
  { icon: Link2, label: "Personal link, short code, and click dashboard" },
] as const;

const PROGRAM_RULES = [
  "Disclose your affiliate relationship clearly.",
  "Use accurate product and pricing claims.",
  "No self-referrals, fake accounts, or cookie stuffing.",
  "No ChickyTutor brand-keyword bidding or impersonation.",
  "No spam, forced redirects, or misleading coupon pages.",
  "Refunded and charged-back payments do not earn commission.",
] as const;

const FAQS = [
  {
    question: "Who can join?",
    answer:
      "Creators, educators, community builders, publishers, and performance marketers with an audience interested in language learning, travel, study, or international life.",
  },
  {
    question: "What counts as a commission?",
    answer:
      "You earn 15% of net collected subscription revenue from an eligible referred customer during their first 12 months. Taxes, refunds, chargebacks, discounts, and payment or marketplace fees are excluded.",
  },
  {
    question: "Can I use paid advertising?",
    answer:
      "Yes, if your ads are accurate and clearly identify your affiliate relationship. Brand-keyword bidding, impersonation, cookie stuffing, spam, and misleading claims are not allowed.",
  },
  {
    question: "When do I get paid?",
    answer:
      "Validated balances are paid monthly after they reach €50. Each commission has a 30-day validation period to account for refunds and chargebacks. Secure payment setup will open when the payout provider is connected.",
  },
] as const;

function AffiliateCta({
  placement,
  className = "",
  compactOnMobile = false,
}: {
  placement: string;
  className?: string;
  compactOnMobile?: boolean;
}) {
  const posthog = usePostHog();
  const label = "Get my affiliate link";

  return (
    <a
      href={AFFILIATE_CTA_HREF}
      onClick={() =>
        trackEventWithAttribution(posthog, "affiliate_program_cta_clicked", {
          placement,
          signup_mode: "self_service",
          commission_percent: AFFILIATE_PROGRAM.commissionPercent,
        })
      }
      className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-[#FD954F] px-6 py-3 font-bold text-[#1C1C1E] shadow-[0_12px_32px_rgba(192,92,47,0.24)] transition duration-200 hover:-translate-y-0.5 hover:bg-[#F28747] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#FD954F]/30 ${className}`}
    >
      {compactOnMobile ? (
        <>
          <span className="sm:hidden">Join program</span>
          <span className="hidden sm:inline">{label}</span>
        </>
      ) : (
        label
      )}
      <ArrowRight className="h-4 w-4" aria-hidden="true" />
    </a>
  );
}

export default function AffiliatePage() {
  const reduceMotion = useReducedMotion();

  return (
    <main className="min-h-screen bg-[#F5F5F0] text-[#1C1C1E]">
      <header className="sticky top-0 z-50 border-b border-[#1C1C1E]/10 bg-[#F5F5F0]/95 backdrop-blur-md">
        <div className="mx-auto flex min-h-20 max-w-7xl items-center justify-between gap-4 px-5 sm:px-8">
          <Link href="/" aria-label="ChickyTutor home" className="shrink-0">
            <BrandLockup surface="light" className="h-auto w-[132px] sm:w-[168px]" priority />
          </Link>
          <nav className="hidden items-center gap-7 text-sm font-semibold text-[#5E625F] md:flex">
            <a href="#product" className="transition hover:text-[#1C1C1E]">
              The product
            </a>
            <a href="#how-it-works" className="transition hover:text-[#1C1C1E]">
              How it works
            </a>
            <a href="#terms" className="transition hover:text-[#1C1C1E]">
              Program rules
            </a>
          </nav>
          <AffiliateCta placement="header" className="px-3 text-sm sm:px-5" compactOnMobile />
        </div>
      </header>

      <section
        className="overflow-hidden border-b border-[#1C1C1E]/10"
        aria-labelledby="affiliate-title"
      >
        <div className="mx-auto grid max-w-[1440px] items-center gap-10 px-5 py-10 sm:px-8 sm:py-14 lg:min-h-[calc(100vh-5rem)] lg:grid-cols-[0.88fr_1.12fr] lg:gap-14 lg:py-16">
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.5 }}
            className="relative z-10"
          >
            <p className="text-sm font-black uppercase tracking-[0.18em] text-[#C05C2F]">
              ChickyTutor affiliate program
            </p>
            <h1
              id="affiliate-title"
              className="mt-5 max-w-[11ch] text-5xl font-black leading-[0.94] tracking-[-0.055em] sm:text-6xl lg:text-[4.75rem]"
            >
              Help your audience find their voice.
            </h1>
            <p className="mt-6 max-w-[34rem] text-lg leading-8 text-[#555B57] sm:text-xl">
              Share real speaking practice and earn 15% when an eligible learner subscribes.
            </p>
            <div className="mt-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
              <AffiliateCta placement="hero" className="w-full sm:w-auto" />
              <a
                href="#product"
                className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full border border-[#1C1C1E]/20 px-6 py-3 font-bold transition hover:border-[#1C1C1E]/40 hover:bg-white sm:w-auto"
              >
                See what you will share
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={reduceMotion ? false : { opacity: 0, x: 22 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.55, delay: reduceMotion ? 0 : 0.08 }}
            className="relative overflow-hidden rounded-[2.25rem] border border-[#1C1C1E]/10 bg-[#E9E5D7] shadow-[0_30px_80px_rgba(28,28,30,0.16)]"
          >
            <Image
              src="/ui/brand-kit/web/chickytutor-affiliate-hero.webp"
              alt="ChickyTutor mascot recording helpful creator content with a microphone and phone"
              width={1536}
              height={1024}
              priority
              sizes="(max-width: 1024px) 100vw, 58vw"
              className="aspect-[3/2] h-full w-full object-cover"
            />
          </motion.div>
        </div>
      </section>

      <section className="bg-[#1C1C1E] text-white" aria-label="Standard affiliate offer">
        <div className="mx-auto grid max-w-7xl divide-y divide-white/15 px-5 sm:grid-cols-2 sm:divide-x sm:divide-y-0 sm:px-8 lg:grid-cols-4">
          {OFFER_FACTS.map((fact) => (
            <div key={fact.label} className="py-7 sm:px-7 sm:first:pl-0 lg:py-8">
              <p className="text-3xl font-black tracking-[-0.035em] text-[#FD954F]">{fact.value}</p>
              <p className="mt-2 max-w-[18rem] text-sm leading-6 text-white/70">{fact.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section
        id="product"
        className="scroll-mt-24 bg-white py-20 sm:py-28"
        aria-labelledby="product-title"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
            <motion.div
              initial={reduceMotion ? false : { opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
            >
              <p className="text-sm font-black uppercase tracking-[0.18em] text-[#C05C2F]">
                A product worth showing
              </p>
              <h2
                id="product-title"
                className="mt-4 max-w-[13ch] text-4xl font-black leading-[1.02] tracking-[-0.045em] sm:text-5xl"
              >
                Show people what speaking practice feels like.
              </h2>
              <p className="mt-5 max-w-[36rem] text-lg leading-8 text-[#5E625F]">
                ChickyTutor gives learners a private place to speak, make mistakes, hear useful
                help, and try again.
              </p>
            </motion.div>

            <div className="divide-y divide-[#1C1C1E]/10 border-y border-[#1C1C1E]/10">
              {PRODUCT_POINTS.map(({ icon: Icon, title, description }, index) => (
                <motion.div
                  key={title}
                  initial={reduceMotion ? false : { opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.45 }}
                  transition={{ delay: reduceMotion ? 0 : index * 0.06 }}
                  className="grid gap-3 py-5 sm:grid-cols-[auto_0.65fr_1.35fr] sm:items-center sm:gap-5"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FD954F]/20 text-[#A84821]">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <h3 className="font-black">{title}</h3>
                  <p className="leading-7 text-[#676B68]">{description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            className="mt-12 overflow-hidden rounded-[2rem] border border-[#1C1C1E]/10 bg-[#0F0F14] shadow-[0_26px_70px_rgba(28,28,30,0.2)]"
          >
            <DemoVideo
              src="/ui/chickytutor-how-it-works4.mp4"
              poster="/ui/chickytutor-how-it-works4-thumbnail.jpg"
              analyticsContext={{
                demoId: "affiliate_product_demo_v1",
                demoKind: "product_video",
                pagePath: "/affiliate",
                pageFamily: "affiliate_program",
                placement: "product_explainer",
              }}
            />
          </motion.div>

          <div className="mt-6 grid gap-6 lg:grid-cols-[1.25fr_0.75fr]">
            <blockquote className="flex min-h-72 flex-col justify-between bg-[#1C1C1E] p-7 text-white sm:p-10">
              <p className="max-w-[24ch] text-2xl font-bold leading-snug tracking-[-0.025em] sm:text-3xl">
                “It got me over the first hump to actually speak Dutch.”
              </p>
              <footer className="mt-8 text-sm text-white/60">
                <cite className="not-italic font-bold text-white">Dutch learner</cite>
                <p className="mt-1">Learner feedback</p>
              </footer>
            </blockquote>
            <div className="flex min-h-72 flex-col justify-between bg-[#F5F5F0] p-7 sm:p-10">
              <p className="text-sm font-black uppercase tracking-[0.18em] text-[#C05C2F]">
                Your best proof
              </p>
              <p className="mt-8 text-2xl font-black leading-snug tracking-[-0.025em]">
                Use the real product. Let your audience hear the conversation.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section
        className="border-y border-[#1C1C1E]/10 bg-[#E9EFE7] py-20 sm:py-28"
        aria-labelledby="angles-title"
      >
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.18em] text-[#46624C]">
              Find your angle
            </p>
            <h2
              id="angles-title"
              className="mt-4 max-w-[12ch] text-4xl font-black leading-[1.02] tracking-[-0.045em] sm:text-5xl"
            >
              Start with a moment your audience knows.
            </h2>
            <p className="mt-5 max-w-[32rem] text-lg leading-8 text-[#58625B]">
              Good affiliate content helps someone recognise a problem and see how ChickyTutor fits
              into real life.
            </p>
          </div>

          <div className="divide-y divide-[#1C1C1E]/10 border-y border-[#1C1C1E]/10">
            {PROMOTION_ANGLES.map((angle, index) => (
              <motion.article
                key={angle.audience}
                initial={reduceMotion ? false : { opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ delay: reduceMotion ? 0 : index * 0.06 }}
                className="grid gap-3 py-7 sm:grid-cols-[0.75fr_1.25fr] sm:gap-8"
              >
                <h3 className="text-lg font-black">{angle.audience}</h3>
                <p className="leading-7 text-[#58625B]">{angle.copy}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28" aria-labelledby="toolkit-title">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[1.08fr_0.92fr] lg:items-center">
          <div className="relative min-h-[460px] overflow-hidden rounded-[2rem] bg-[#1C1C1E] p-5 sm:min-h-[560px] sm:p-8">
            <div className="absolute inset-x-0 top-0 h-1 bg-[#FD954F]" />
            <div className="relative h-[280px] overflow-hidden rounded-2xl border border-white/10 bg-[#F5F5F0] sm:h-[350px]">
              <Image
                src="/ui/Ads_demo_screenshot.png"
                alt="ChickyTutor speaking practice interface"
                fill
                sizes="(max-width: 1024px) 100vw, 54vw"
                className="object-contain p-4 sm:p-7"
              />
            </div>
            <div className="absolute bottom-5 left-5 w-[48%] overflow-hidden rounded-2xl border border-white/10 bg-white p-4 shadow-2xl sm:bottom-8 sm:left-8 sm:p-6">
              <Image
                src="/ui/brand-kit/lockups/chickytutor-lockup-horizontal-light-1080.png"
                alt="ChickyTutor logo"
                width={1080}
                height={300}
                className="h-auto w-full"
              />
            </div>
            <div className="absolute bottom-5 right-5 w-[42%] overflow-hidden rounded-2xl border-4 border-[#1C1C1E] bg-[#F5F5F0] shadow-2xl sm:bottom-8 sm:right-8">
              <Image
                src="/ui/chickytutor-how-it-works4-thumbnail.jpg"
                alt="ChickyTutor product demo thumbnail"
                width={1920}
                height={1080}
                className="aspect-video h-auto w-full object-cover"
              />
            </div>
          </div>

          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <p className="text-sm font-black uppercase tracking-[0.18em] text-[#C05C2F]">
              Your creator toolkit
            </p>
            <h2
              id="toolkit-title"
              className="mt-4 max-w-[12ch] text-4xl font-black leading-[1.02] tracking-[-0.045em] sm:text-5xl"
            >
              Your link comes with material you can use.
            </h2>
            <p className="mt-5 max-w-[34rem] text-lg leading-8 text-[#5E625F]">
              Join once, then open your dashboard for approved assets, clear claims, and practical
              starter copy.
            </p>
            <div className="mt-8 divide-y divide-[#1C1C1E]/10 border-y border-[#1C1C1E]/10">
              {TOOLKIT_ITEMS.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-4 py-4">
                  <Icon className="h-5 w-5 shrink-0 text-[#C05C2F]" aria-hidden="true" />
                  <span className="font-bold leading-6">{label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section
        id="how-it-works"
        className="scroll-mt-24 bg-[#F5F5F0] py-20 sm:py-28"
        aria-labelledby="how-title"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid gap-12 lg:grid-cols-[0.75fr_1.25fr]">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.18em] text-[#C05C2F]">
                How it works
              </p>
              <h2
                id="how-title"
                className="mt-4 max-w-[11ch] text-4xl font-black leading-[1.02] tracking-[-0.045em] sm:text-5xl"
              >
                Join today. Build from one clear link.
              </h2>
              <p className="mt-5 max-w-[32rem] text-lg leading-8 text-[#5E625F]">
                Your profile, terms, link, resources, and click reporting are ready now.
                Provider-backed commissions and payouts are still being connected.
              </p>
            </div>

            <ol className="divide-y divide-[#1C1C1E]/10 border-y border-[#1C1C1E]/10">
              {HOW_IT_WORKS.map(({ icon: Icon, title, description }, index) => (
                <motion.li
                  key={title}
                  initial={reduceMotion ? false : { opacity: 0, x: 18 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ delay: reduceMotion ? 0 : index * 0.06 }}
                  className="grid gap-4 py-6 sm:grid-cols-[3rem_0.6fr_1.4fr] sm:items-center sm:gap-6"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1C1C1E] text-[#FD954F]">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#8B8F8C]">
                      Step {index + 1}
                    </p>
                    <h3 className="mt-1 text-xl font-black">{title}</h3>
                  </div>
                  <p className="leading-7 text-[#5E625F]">{description}</p>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section
        id="terms"
        className="scroll-mt-24 bg-[#1C1C1E] py-20 text-white sm:py-28"
        aria-labelledby="rules-title"
      >
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.18em] text-[#FD954F]">
              Clear rules from day one
            </p>
            <h2
              id="rules-title"
              className="mt-4 max-w-[11ch] text-4xl font-black leading-[1.02] tracking-[-0.045em] sm:text-5xl"
            >
              Recommend honestly. Protect the trust you built.
            </h2>
            <p className="mt-5 max-w-[34rem] text-lg leading-8 text-white/70">
              Useful, accurate promotion is welcome. The rules protect learners, affiliates, and the
              ChickyTutor brand.
            </p>
            <Link
              href="/affiliate/terms"
              className="mt-7 inline-flex min-h-11 items-center gap-2 font-bold text-[#FD954F] transition hover:text-white"
            >
              Read the full affiliate terms
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>

          <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
            {PROGRAM_RULES.map((rule) => (
              <div key={rule} className="flex gap-3 border-t border-white/15 pt-5">
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#FD954F]" aria-hidden="true" />
                <span className="leading-7 text-white/80">{rule}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28" aria-labelledby="faq-title">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <p className="text-sm font-black uppercase tracking-[0.18em] text-[#C05C2F]">FAQ</p>
          <h2 id="faq-title" className="mt-4 text-4xl font-black tracking-[-0.045em] sm:text-5xl">
            The practical details
          </h2>
          <div className="mt-10 divide-y divide-[#1C1C1E]/10 border-y border-[#1C1C1E]/10">
            {FAQS.map(({ question, answer }) => (
              <details key={question} className="group py-6">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-6 text-lg font-black focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#FD954F]/25">
                  {question}
                  <span className="text-2xl font-light text-[#C05C2F] transition group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="max-w-3xl pt-4 leading-7 text-[#5E625F]">{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section
        className="border-t border-[#1C1C1E]/10 bg-[#E9EFE7] py-20 sm:py-24"
        aria-labelledby="final-title"
      >
        <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
          <Image
            src="/ui/brand-kit/web/chickytutor-face-mark-512.webp"
            alt=""
            width={512}
            height={512}
            className="mx-auto h-16 w-16"
          />
          <h2
            id="final-title"
            className="mx-auto mt-5 max-w-[14ch] text-4xl font-black leading-[1.02] tracking-[-0.045em] sm:text-5xl"
          >
            Ready to share something useful?
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-[#58625B]">
            Create your profile, copy your link, and introduce your audience to real speaking
            practice.
          </p>
          <AffiliateCta placement="final" className="mt-8 w-full sm:w-auto" />
        </div>
      </section>

      <footer className="border-t border-[#1C1C1E]/10 bg-[#F5F5F0]">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 px-5 py-8 text-sm text-[#676B68] sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <div className="flex items-center gap-4">
            <BrandLockup surface="light" className="h-auto w-[140px]" />
            <span>© {new Date().getFullYear()}</span>
          </div>
          <div className="flex flex-wrap gap-5">
            <Link href="/affiliate/terms" className="transition hover:text-[#1C1C1E]">
              Affiliate terms
            </Link>
            <Link href="/privacy" className="transition hover:text-[#1C1C1E]">
              Privacy
            </Link>
            <Link href="/terms" className="transition hover:text-[#1C1C1E]">
              General terms
            </Link>
          </div>
        </div>
      </footer>
    </main>
  );
}
